import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ShieldCheck, Loader2, LogOut, CheckCircle2, XCircle, ExternalLink, Inbox, MessageSquareReply, Mail, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { RequestsPanel } from "@/components/RequestsPanel";
import { LaunchPanel } from "@/components/LaunchPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pb_admin_token";
const TABS = [
  { id: "pending_verification", label: "Pending" },
  { id: "published", label: "Published" },
  { id: "rejected", label: "Rejected" },
  { id: "requests", label: "Requests" },
  { id: "launch", label: "Launch" },
];

const formatError = (d) =>
  typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg || "").join(" ") : "Something went wrong.";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginState, setLoginState] = useState({ state: "idle", msg: "" });
  const [showPw, setShowPw] = useState(false);
  const [tab, setTab] = useState("pending_verification");
  const [reviews, setReviews] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [actionError, setActionError] = useState("");
  const [launchState, setLaunchState] = useState({ state: "idle", msg: "" });

  const authed = axios.create({ headers: { Authorization: `Bearer ${token}` } });

  const load = useCallback(async () => {
    if (!token) return;
    if (tab === "requests" || tab === "launch") {
      setReviews([]);
      return;
    }
    try {
      const { data } = await authed.get(`${API}/admin/reviews`, { params: { status: tab } });
      setReviews(data.reviews);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
      setReviews([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tab]);

  useEffect(() => {
    setReviews(null);
    load();
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoginState({ state: "loading", msg: "" });
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setLoginState({ state: "error", msg: formatError(err.response?.data?.detail) });
    }
  };

  const forgot = async () => {
    const target = email.trim() || "support@proofbuy.in";
    setLoginState({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: target });
      setLoginState({ state: "success", msg: `Reset link sent to ${target} — check the inbox (and spam).` });
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginState({ state: "error", msg: typeof d === "string" ? d : "Could not send — try again shortly." });
    }
  };

  const act = async (id, action) => {
    setBusyId(id + action);
    setActionError("");
    try {
      await authed.post(`${API}/admin/reviews/${id}/${action}`);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setActionError(typeof d === "string" ? d : "Action failed — try again.");
    } finally {
      setBusyId("");
    }
  };

  const sendLaunch = async () => {
    if (!window.confirm("Email the entire founding waitlist now?")) return;
    setLaunchState({ state: "loading", msg: "" });
    try {
      const { data } = await authed.post(`${API}/admin/launch-email`);
      setLaunchState({ state: "done", msg: `Launch email sent to ${data.sent}${data.failed ? `, ${data.failed} failed` : ""}.` });
    } catch {
      setLaunchState({ state: "done", msg: "Launch email failed — try again." });
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-6">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-sm rounded-2xl bg-white border border-black/5 shadow-lg p-8">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-lg bg-trust flex items-center justify-center">
              <ShieldCheck size={18} className="text-white" />
            </span>
            <span className="font-display font-bold text-xl">ProofBuy Admin</span>
          </div>
          <p className="mt-2 text-sm text-gray-500">Verification queue — sign in to review receipts.</p>
          <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="support@proofbuy.in" className="mt-6 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-trust" />
          <div className="relative mt-3">
            <input data-testid="admin-password-input" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" className="w-full rounded-xl border border-gray-200 px-4 py-3 pr-11 text-sm outline-none focus:border-trust" />
            <button type="button" data-testid="admin-password-toggle" onClick={() => setShowPw(!showPw)} aria-label="Toggle password visibility"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-trust transition-colors duration-200">
              {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
          <div className="mt-2.5 text-right">
            <button type="button" data-testid="admin-forgot-link" onClick={forgot}
              className="text-xs font-semibold text-gray-400 hover:text-trust transition-colors duration-200">
              Forgot password?
            </button>
          </div>
          <button data-testid="admin-login-submit" type="submit" disabled={loginState.state === "loading"}
            className="mt-3 w-full rounded-full bg-trust text-white font-semibold py-3 transition-colors duration-200 hover:bg-trust-dark disabled:opacity-60 flex items-center justify-center gap-2">
            {loginState.state === "loading" ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
          </button>
          {loginState.msg && (
            <p data-testid="admin-login-error" className={`mt-3 text-sm font-semibold ${loginState.state === "success" ? "text-verified" : "text-warn"}`}>
              {loginState.msg}
            </p>
          )}
          <a data-testid="admin-login-back-link" href="/"
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-trust transition-colors duration-200">
            <ArrowLeft size={13} /> Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a data-testid="admin-back-link" href="/"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-trust transition-colors duration-200">
              <ArrowLeft size={15} /> Back to site
            </a>
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-lg bg-trust flex items-center justify-center">
                <ShieldCheck size={16} className="text-white" />
              </span>
              <span className="font-display font-bold text-lg">Verification queue</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {launchState.msg && <span data-testid="launch-mailer-status" className="text-xs font-semibold text-gray-500">{launchState.msg}</span>}
            <button data-testid="admin-launch-mailer-button" onClick={sendLaunch} disabled={launchState.state === "loading"}
              className="inline-flex items-center gap-2 rounded-full bg-trust text-white px-4 py-2 text-xs font-semibold transition-colors duration-200 hover:bg-trust-dark disabled:opacity-60">
              {launchState.state === "loading" ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email waitlist
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-warn transition-colors duration-200">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                tab === t.id ? "bg-trust text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-trust/50"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {actionError && (
          <p data-testid="admin-action-error" className="mt-5 rounded-xl bg-warn-soft text-warn px-5 py-3 text-sm font-semibold">{actionError}</p>
        )}

        {tab !== "requests" && tab !== "launch" && (
        <div className="mt-8 space-y-5">
          {reviews === null ? (
            <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-trust" /></div>
          ) : reviews.length === 0 ? (
            <div data-testid="admin-empty-state" className="rounded-2xl bg-white border border-black/5 p-12 text-center">
              <Inbox size={28} className="mx-auto text-gray-300" />
              <p className="mt-3 text-sm text-gray-500">No {TABS.find((t) => t.id === tab)?.label.toLowerCase()} reviews right now.</p>
            </div>
          ) : (
            reviews.map((r) => (
              <article key={r.id} data-testid={`admin-review-card-${r.id}`} className="rounded-2xl bg-white border border-black/5 shadow-sm p-6 sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="font-display text-xl font-semibold tracking-tight">{r.product_name}</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {r.seller_name} · {r.category === "course" ? "Course / webinar" : "Digital product"} · paid <b className="text-ink">{r.price_paid}</b>
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="rounded-xl bg-paper p-4"><p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5">Promised</p>{r.promised}</div>
                  <div className="rounded-xl bg-paper p-4"><p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5">Received</p>{r.received}</div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                  <span className={`rounded-full px-3 py-1.5 ${r.upsell_pushed === "yes" ? "bg-warn-soft text-warn" : "bg-gray-100 text-gray-500"}`}>Upsell: {r.upsell_pushed}</span>
                  <span className={`rounded-full px-3 py-1.5 ${r.refund_offered === "yes" ? "bg-verified-soft text-verified" : "bg-gray-100 text-gray-500"}`}>Refund: {r.refund_offered}</span>
                  <span className={`rounded-full px-3 py-1.5 ${r.resell_rights === "yes" ? "bg-gold-soft text-yellow-700" : "bg-gray-100 text-gray-500"}`}>Resell-rights: {r.resell_rights}</span>
                  {r.reviewer_email && <span className="rounded-full px-3 py-1.5 bg-gray-100 text-gray-500">{r.reviewer_email}</span>}
                </div>
                {r.seller_response?.status === "pending" && (
                  <div data-testid={`admin-response-pending-${r.id}`} className="mt-5 rounded-xl border border-gold/50 bg-gold-soft p-5">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-yellow-700">
                      <MessageSquareReply size={14} /> Seller response — pending moderation
                    </p>
                    <p className="mt-2 text-sm text-gray-700">{r.seller_response.text}</p>
                    <p className="mt-2 text-xs text-gray-500">
                      {r.seller_response.responder_name} · {r.seller_response.responder_email}
                    </p>
                    {r.seller_response.email_verified ? (
                      <p className="mt-2 text-xs font-bold text-verified">Email confirmed — identity check passed</p>
                    ) : (
                      <p className="mt-2 text-xs font-bold text-yellow-700">Waiting for them to confirm their email — approval unlocks after that</p>
                    )}
                    <div className="mt-3 flex gap-2">
                      <button data-testid={`admin-response-approve-${r.id}`} onClick={() => act(r.id, "response/approve")} disabled={!!busyId}
                        className="inline-flex items-center gap-1.5 rounded-full bg-verified text-white px-4 py-2 text-xs font-semibold transition-opacity duration-200 hover:opacity-90 disabled:opacity-50">
                        {busyId === r.id + "response/approve" ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Approve response
                      </button>
                      <button data-testid={`admin-response-reject-${r.id}`} onClick={() => act(r.id, "response/reject")} disabled={!!busyId}
                        className="inline-flex items-center gap-1.5 rounded-full bg-warn text-white px-4 py-2 text-xs font-semibold transition-opacity duration-200 hover:opacity-90 disabled:opacity-50">
                        {busyId === r.id + "response/reject" ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />} Reject response
                      </button>
                    </div>
                  </div>
                )}
                {r.seller_response?.status === "approved" && (
                  <div className="mt-5 rounded-xl border border-verified/25 bg-verified-soft p-5">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-verified">
                      <MessageSquareReply size={14} /> Seller response — live
                    </p>
                    <p className="mt-2 text-sm text-gray-700">{r.seller_response.text}</p>
                  </div>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <a data-testid={`admin-proof-payment-${r.id}`} href={`${API}/files/${r.payment_proof_path}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 hover:border-trust hover:text-trust transition-colors duration-200">
                    <ExternalLink size={13} /> Payment proof
                  </a>
                  <a data-testid={`admin-proof-product-${r.id}`} href={`${API}/files/${r.product_proof_path}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 hover:border-trust hover:text-trust transition-colors duration-200">
                    <ExternalLink size={13} /> Product proof
                  </a>
                  <span className="flex-1" />
                  {tab !== "published" && (
                    <button data-testid={`admin-publish-${r.id}`} onClick={() => act(r.id, "publish")} disabled={!!busyId}
                      className="inline-flex items-center gap-1.5 rounded-full bg-verified text-white px-5 py-2.5 text-sm font-semibold transition-opacity duration-200 hover:opacity-90 disabled:opacity-50">
                      {busyId === r.id + "publish" ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Publish
                    </button>
                  )}
                  {tab !== "rejected" && (
                    <button data-testid={`admin-reject-${r.id}`} onClick={() => act(r.id, "reject")} disabled={!!busyId}
                      className="inline-flex items-center gap-1.5 rounded-full bg-warn text-white px-5 py-2.5 text-sm font-semibold transition-opacity duration-200 hover:opacity-90 disabled:opacity-50">
                      {busyId === r.id + "reject" ? <Loader2 size={15} className="animate-spin" /> : <XCircle size={15} />} Reject
                    </button>
                  )}
                </div>
              </article>
            ))
          )}
        </div>
        )}
        {tab === "requests" && (
          <div className="mt-8">
            <RequestsPanel token={token} />
          </div>
        )}
        {tab === "launch" && (
          <div className="mt-8">
            <LaunchPanel token={token} />
          </div>
        )}
      </main>
    </div>
  );
}
